// 관리자 전용 DTO/스키마 (회원관리 + 통계). Phase 4-F.

import { z } from "zod";
import { couponSpecSchema } from "./coupon.js";
import type { OrderType } from "./orders.js";

// 관리자 회원 목록/상세 1건.
export interface AdminUserDto {
  id: string;
  displayName: string | null;
  phone: string;
  tierKey: string;
  tierName: string;
  pointsBalance: number;
  // 평생 누적 구매액(기간 제한 없음). 수령완료 시점마다 누적되는 참고용 통계값.
  totalSpentKrw: number;
  // 등급 산정 기준 실적(최근 N개월 윈도우 합계)과 그 윈도우 길이(개월).
  //  - 상세 조회에서만 채워진다(목록에서는 undefined). 누적 구매액과 구분해 표시하기 위함.
  tierSpentKrw?: number;
  tierWindowMonths?: number;
  noShowCount: number;
  isRestricted: boolean;
  // 마케팅 수신 동의(이벤트/혜택 알림 푸시 / SMS). 가입 시 선택 동의값.
  marketingPush: boolean;
  marketingSms: boolean;
  createdAt: string;
}

// 전체 회원 일괄 쿠폰 지급(관리자). couponSpec 1건을 모든 활성 회원에게 count 만큼 발급.
export const adminBulkGrantCouponInputSchema = couponSpecSchema;
export type AdminBulkGrantCouponInput = z.infer<typeof adminBulkGrantCouponInputSchema>;

// 적립금 수동 조정 (관리자). amount 부호: 지급(+)/차감(-). 차감은 잔액까지만 적용된다(서버 보정).
export const adminAdjustPointsInputSchema = z.object({
  amount: z.number().int().refine((v) => v !== 0, "0이 아닌 값을 입력하세요."),
  reason: z.string().trim().min(1, "사유를 입력하세요.").max(100),
});
export type AdminAdjustPointsInput = z.infer<typeof adminAdjustPointsInputSchema>;

// 쿠폰 수동 지급 (관리자). couponSpec 1건을 count 만큼 발급.
export const adminGrantCouponInputSchema = couponSpecSchema;
export type AdminGrantCouponInput = z.infer<typeof adminGrantCouponInputSchema>;

// 이용 제한 토글.
export const adminSetRestrictedInputSchema = z.object({
  restricted: z.boolean(),
});
export type AdminSetRestrictedInput = z.infer<typeof adminSetRestrictedInputSchema>;

// 간단 통계 (대시보드).
export interface AdminStatsDto {
  // 완료(수령완료) 주문 기준 집계.
  today: { orders: number; salesKrw: number };
  week: { orders: number; salesKrw: number };
  // 누적 판매량 상위 상품.
  bestProducts: { id: string; name: string; salesCount: number }[];
  // 현재 신규(미처리) 주문 수.
  pendingOrders: number;
}

// 매출관리 화면용 완료 주문 1건 요약.
// 완료(수령완료)로 종결된 주문만 매출로 잡으며, 날짜 기준은 completedAt(완료 종결 시각, KST 해석)이다.
export interface SalesOrderDto {
  id: string;
  orderNo: string;
  type: OrderType;
  totalKrw: number;
  itemCount: number;
  customerName: string | null;
  completedAt: string; // ISO 8601 (UTC)
}

// 매출 조회 기간 필터. from/to 는 KST 기준 날짜(YYYY-MM-DD, 둘 다 포함).
// 둘 다 생략하면 전체 완료 주문을 반환한다.
export const adminSalesQuerySchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, "from 은 YYYY-MM-DD 형식이어야 합니다.")
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, "to 는 YYYY-MM-DD 형식이어야 합니다.")
    .optional(),
});
export type AdminSalesQuery = z.infer<typeof adminSalesQuerySchema>;
